import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { teamRoleLabel } from "../../lib/labels";

type UnlinkedMember = {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  membership_role: string;
  joined_at: string;
};

export function LinkUserCard({
  teamId,
  playerName,
  linkedEmail,
  canManage,
  onLink,
  onUnlink,
  isLinking,
  isUnlinking,
}: {
  teamId: string;
  playerName: string;
  linkedEmail: string | null;
  canManage: boolean;
  onLink: (email: string) => void;
  onUnlink: () => void;
  isLinking: boolean;
  isUnlinking: boolean;
}) {
  const { t } = useTranslation("players");
  const [open, setOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [manualEmail, setManualEmail] = useState("");
  const [showManual, setShowManual] = useState(false);

  // Lista dedicata al collegamento: a differenza di list_unlinked_team_members
  // (Richieste, avvisi) include founder, coach e membri ignorati.
  const unlinkedQuery = useQuery({
    queryKey: ["linkable-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_linkable_team_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as UnlinkedMember[];
    },
    enabled: open && canManage,
    staleTime: 30_000,
  });

  if (linkedEmail) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">{t("link.linked")}</p>
            <p className="text-sm font-medium truncate">{linkedEmail}</p>
          </div>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (window.confirm(t("link.unlinkConfirm", { name: playerName }))) onUnlink();
              }}
              disabled={isUnlinking}
            >
              {t("link.unlink")}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!canManage) return null;

  const unlinked = unlinkedQuery.data || [];
  const selectedMember = unlinked.find((m) => m.email === selectedEmail);

  const submitDropdown = () => {
    if (!selectedEmail) return;
    onLink(selectedEmail);
    setOpen(false);
    setSelectedEmail("");
  };

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualEmail.trim()) return;
    onLink(manualEmail.trim());
    setOpen(false);
    setManualEmail("");
    setShowManual(false);
  };

  return (
    <Card className="shadow-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <p className="text-xs eb-eyebrow">{t("link.linked")}</p>
            <p className="text-sm text-muted-foreground">
              {t("link.noAccount")}
            </p>
          </div>
          {!open && (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
              {t("link.linkUser")}
            </Button>
          )}
        </div>
        {open && !showManual && (
          <div className="space-y-2">
            <p className="text-[11px] text-muted-foreground">
              {t("link.unlinkedListHint")}
            </p>
            {unlinkedQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">{t("link.loading")}</p>
            ) : unlinked.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("link.noneAvailable")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                  <SelectTrigger className="h-8 text-sm flex-1 min-w-[240px]">
                    <SelectValue placeholder={t("link.selectUser")} />
                  </SelectTrigger>
                  <SelectContent>
                    {unlinked.map((m) => {
                      const name = m.full_name && m.full_name !== m.email
                        ? `${m.full_name} · ${m.email}`
                        : m.email;
                      // Lo staff si riconosce: "· Founder", "· Allenatore".
                      const display = m.membership_role === "founder" || m.membership_role === "coach"
                        ? `${name} · ${teamRoleLabel(m.membership_role)}`
                        : name;
                      return (
                        <SelectItem key={m.user_id} value={m.email}>
                          {display}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-8"
                  disabled={isLinking || !selectedEmail}
                  onClick={submitDropdown}
                >
                  {isLinking ? t("link.linking") : t("link.confirm")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8"
                  onClick={() => {
                    setOpen(false);
                    setSelectedEmail("");
                  }}
                >
                  {t("link.cancel")}
                </Button>
              </div>
            )}
            {selectedMember && (
              <p className="text-[11px] text-muted-foreground">
                {t("link.previewLink", { full: selectedMember.full_name, email: selectedMember.email, player: playerName })}
              </p>
            )}
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
              onClick={() => setShowManual(true)}
            >
              {t("link.manualToggle")}
            </button>
          </div>
        )}
        {open && showManual && (
          <form className="space-y-2" onSubmit={submitManual}>
            <p className="text-[11px] text-muted-foreground">
              {t("link.manualIntro")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <Input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                placeholder={t("link.emailPh")}
                className="h-8 flex-1 min-w-[200px] text-sm"
                required
                autoFocus
              />
              <Button type="submit" size="sm" className="h-8" disabled={isLinking || !manualEmail.trim()}>
                {isLinking ? t("link.linking") : t("link.confirm")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8"
                onClick={() => {
                  setShowManual(false);
                  setManualEmail("");
                }}
              >
                {t("link.backToList")}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
